import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, LogOut, Settings, Trophy, CheckCircle, Circle, Archive, Flame, Search, BookOpen } from 'lucide-react';
import { useTutorial } from '../components/tutorial/TutorialContext';
import TutorialLaunchButton from '../components/tutorial/TutorialLaunchButton';
import { ReferenceDocsPanel } from '../components/ReferenceDocsPanel';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import AutoUsageMeter from '../components/AutoUsageMeter';
import NamePromptModal, {
  hasDismissedNamePrompt,
  markNamePromptDismissed,
} from '../components/NamePromptModal';
import PolicyAcknowledgmentModal from '../components/PolicyAcknowledgmentModal';
import SiteFooter from '../components/SiteFooter';
import OuraCard from '../components/OuraCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Appearance picker rendered inside the Settings panel. Two labelled
 * pills (Dark / Light) that swap the theme. Discoverable alternative
 * to the compact Sun/Moon header toggle for first-time users.
 */
const AppearancePicker = () => {
  const { theme, setTheme } = useTheme();
  const options = [
    { key: 'dark', label: 'Dark', hint: 'Obsidian' },
    { key: 'light', label: 'Light', hint: 'Linen' },
  ];
  return (
    <div data-testid="appearance-picker" className="flex items-center justify-between">
      <div>
        <p className="text-sm text-white">Appearance</p>
        <p className="text-xs text-zinc-500">
          Switch between SAGE's native obsidian and a warm linen palette.
        </p>
      </div>
      <div className="flex gap-2">
        {options.map(({ key, label, hint }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTheme(key)}
            data-testid={`appearance-${key}`}
            className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider transition-all ${
              theme === key
                ? 'bg-amber-500/20 text-amber-100 border border-amber-500/50 shadow-[0_0_0_1px_rgba(184,134,11,0.2)]'
                : 'bg-transparent text-zinc-400 border border-white/10 hover:border-white/20'
            }`}
            title={hint}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

const DashboardNew = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchors, setAnchors] = useState([]);
  const [todos, setTodos] = useState([]);
  const [wins, setWins] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [profile, setProfile] = useState({
    voice_intensity: 50,
    stoic_level: 50,
    directness: 50,
    auto_mode_enabled: true,
    voice_id: '21m00Tcm4TlvDq8ikWAM',
    name: '',
  });
  const [voiceCatalogue, setVoiceCatalogue] = useState([]);
  const [textAnchor, setTextAnchor] = useState({ title: '', content: '' });
  const [showAddText, setShowAddText] = useState(false);
  const saveWeightsTimerRef = useRef(null);
  const { requestAutoLaunch, scriptLoaded } = useTutorial();

  // Policy acknowledgment state. `null` until we've checked; otherwise an
  // object { needsAck: bool, serverVersion: string }. The modal renders
  // only when `needsAck` is true. Independent of the name-prompt modal.
  const [policyState, setPolicyState] = useState(null);
  const [policySubmitting, setPolicySubmitting] = useState(false);

  useEffect(() => {
    fetchUserData();
    fetchAnchors();
    fetchTodos();
    fetchWins();
    checkPolicyAcknowledgment();
    // Mount-only effect: fetchers read from latest closures via axios call.
    // Re-running on identity change of fetchers would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-launch the onboarding tour on first Dashboard mount when the
  // user's `tutorial_completed` flag is false. Fires exactly once per app
  // session — the TutorialContext ref guards against double-firing under
  // React.StrictMode's double-mount.
  useEffect(() => {
    if (scriptLoaded) {
      requestAutoLaunch();
    }
  }, [scriptLoaded, requestAutoLaunch]);

  /**
   * Fetch the live POLICY_VERSION and this user's acknowledgment doc.
   * If the versions don't match, flip `needsAck` on so the modal renders.
   * Silent-fail: a transient network blip shouldn't block the Dashboard.
   */
  const checkPolicyAcknowledgment = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      if (!token) return;
      const [verRes, ackRes] = await Promise.all([
        axios.get(`${API}/policies/version`),
        axios.get(`${API}/policies/me/acknowledgment`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const serverVersion = verRes.data?.version;
      const ack = ackRes.data?.acknowledgment;
      const needsAck =
        !ack ||
        ack.version !== serverVersion ||
        !ack.terms ||
        !ack.privacy ||
        !ack.beta_confidentiality;
      setPolicyState({ needsAck, serverVersion });
    } catch (_e) {
      // Network or auth failure — don't block the Dashboard.
      setPolicyState({ needsAck: false });
    }
  };

  const handlePolicyAgree = async () => {
    if (policySubmitting) return;
    setPolicySubmitting(true);
    try {
      const token = localStorage.getItem('sage_token');
      await axios.post(
        `${API}/policies/acknowledge`,
        {
          version: policyState?.serverVersion,
          terms: true,
          privacy: true,
          beta_confidentiality: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPolicyState({ needsAck: false });
      toast.success('Thank you — you can revisit either policy any time from the footer.');
    } catch (error) {
      const detail = error?.response?.data?.detail || 'Could not record your acknowledgment. Please try again.';
      toast.error(detail);
    } finally {
      setPolicySubmitting(false);
    }
  };

  const handlePolicyDecline = () => {
    // Soft decline — sign the user out so acceptance stays meaningful.
    try {
      localStorage.removeItem('sage_token');
      localStorage.removeItem('sage_user');
    } catch (_e) { /* best-effort */ }
    toast('Signed out. You can return any time from your invite link.', {
      description: 'The policies are available in the footer whenever you want to review them.',
    });
    navigate('/');
  };

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUser(response.data);
      setProfile({
        ...response.data.sage_profile,
        auto_mode_enabled: response.data.auto_mode_enabled !== false,
        voice_id: response.data.voice_id || '21m00Tcm4TlvDq8ikWAM',
        name: response.data.name || '',
      });
      // Fire-and-forget voice catalogue fetch.
      axios
        .get(`${API}/voices`, { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => setVoiceCatalogue(data.voices || []))
        .catch(() => { /* non-fatal — settings dropdown just stays empty */ });
      setLoading(false);
    } catch (error) {
      localStorage.removeItem('sage_token');
      localStorage.removeItem('sage_user');
      navigate('/login');
    }
  };

  const fetchAnchors = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      const response = await axios.get(`${API}/knowledge-anchors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetched = response.data || [];
      // Ensure weights sum to 100 on load; if not, normalize and persist
      const normalized = normalizeWeights(fetched);
      setAnchors(normalized);
      if (normalized.length > 1 && needsNormalization(fetched, normalized)) {
        persistWeights(normalized);
      }
    } catch (error) {
      console.error('Failed to fetch anchors');
    }
  };

  const fetchTodos = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      const response = await axios.get(`${API}/todos?status=pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodos(response.data);
    } catch (error) {
      console.error('Failed to fetch todos');
    }
  };

  const fetchWins = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      const response = await axios.get(`${API}/wins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWins(response.data);
    } catch (error) {
      console.error('Failed to fetch wins');
    }
  };

  const completeTodo = async (todoId) => {
    try {
      const token = localStorage.getItem('sage_token');
      await axios.put(`${API}/todos/${todoId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Congratulations on this win!');
      fetchTodos();
      fetchWins();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleProfileUpdate = async () => {
    try {
      const token = localStorage.getItem('sage_token');
      await axios.put(`${API}/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated');
      setShowSettings(false);
      // Keep the local user object in sync so the name-prompt modal and
      // header greeting both reflect any edits the user just made.
      setUser((prev) => (prev ? { ...prev, name: profile.name || '' } : prev));
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  /**
   * Save handler for the one-time NamePromptModal. Persists the name,
   * updates local state so the modal unmounts, and surfaces a toast.
   */
  const handleSaveNameFromPrompt = async (newName) => {
    try {
      const token = localStorage.getItem('sage_token');
      await axios.put(
        `${API}/profile`,
        { name: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUser((prev) => (prev ? { ...prev, name: newName } : prev));
      setProfile((prev) => ({ ...prev, name: newName }));
      toast.success(`SAGE will call you ${newName}.`);
    } catch (_error) {
      toast.error('Could not save your name. Please try again.');
    }
  };

  const handleSkipNamePrompt = () => {
    markNamePromptDismissed();
    // Flip user.name to a sentinel so React re-renders without the modal
    // without mutating what the server has. Any subsequent /auth/me fetch
    // will reset this to "" — hasDismissedNamePrompt() stays authoritative.
    setUser((prev) => (prev ? { ...prev, _namePromptSkipped: true } : prev));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);

    try {
      const token = localStorage.getItem('sage_token');
      await axios.post(`${API}/knowledge-anchors/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('PDF uploaded successfully');
      fetchAnchors();
    } catch (error) {
      toast.error('Failed to upload PDF');
    }
  };

  const handleTextAnchorSubmit = async () => {
    if (!textAnchor.title || !textAnchor.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const token = localStorage.getItem('sage_token');
      const params = new URLSearchParams({
        title: textAnchor.title,
        content: textAnchor.content,
        source_type: 'text'
      });
      
      await axios.post(`${API}/knowledge-anchors?${params.toString()}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Knowledge anchor added');
      setTextAnchor({ title: '', content: '' });
      setShowAddText(false);
      fetchAnchors();
    } catch (error) {
      toast.error('Failed to add anchor');
    }
  };

  const handleDeleteAnchor = async (anchorId) => {
    try {
      const token = localStorage.getItem('sage_token');
      await axios.delete(`${API}/knowledge-anchors/${anchorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Anchor deleted');
      fetchAnchors();
    } catch (error) {
      toast.error('Failed to delete anchor');
    }
  };

  // ===== Weight allocation helpers (sum must equal 100%) =====
  // Distribute 100 across N items, returning integer weights summing exactly to 100.
  const equalSplit = (n) => {
    if (n <= 0) return [];
    const base = Math.floor(100 / n);
    const remainder = 100 - base * n;
    return Array.from({ length: n }, (_, i) => base + (i < remainder ? 1 : 0));
  };

  // Re-normalize an array of anchors so weights sum to 100 (integers).
  const normalizeWeights = (list) => {
    if (!list || list.length === 0) return [];
    if (list.length === 1) return [{ ...list[0], weight: 100 }];

    const weights = list.map((a) => (typeof a.weight === 'number' ? a.weight : 0));
    const total = weights.reduce((s, w) => s + w, 0);

    let scaled;
    if (total <= 0) {
      scaled = equalSplit(list.length);
    } else {
      // Scale proportionally, then fix rounding to ensure sum=100
      const raw = weights.map((w) => (w / total) * 100);
      scaled = raw.map((r) => Math.floor(r));
      let diff = 100 - scaled.reduce((s, w) => s + w, 0);
      // Distribute remaining points to entries with largest fractional parts
      const fractions = raw
        .map((r, idx) => ({ idx, frac: r - Math.floor(r) }))
        .sort((a, b) => b.frac - a.frac);
      let i = 0;
      while (diff > 0 && i < fractions.length) {
        scaled[fractions[i].idx] += 1;
        diff -= 1;
        i += 1;
      }
    }

    return list.map((a, idx) => ({ ...a, weight: scaled[idx] }));
  };

  const needsNormalization = (orig, normalized) => {
    if (orig.length !== normalized.length) return true;
    const total = orig.reduce((s, a) => s + (a.weight || 0), 0);
    if (total !== 100) return true;
    return orig.some((a, idx) => a.weight !== normalized[idx].weight);
  };

  // Redistribute when one slider changes so total stays at 100.
  // Proportionally reduce/increase the other anchors based on their current weights.
  const redistributeWeights = (list, changedId, newWeight) => {
    if (list.length === 0) return list;
    if (list.length === 1) return [{ ...list[0], weight: 100 }];

    const clampedNew = Math.max(0, Math.min(100, newWeight));
    const others = list.filter((a) => a.anchor_id !== changedId);
    const otherSumBefore = others.reduce((s, a) => s + (a.weight || 0), 0);
    const otherSumTarget = 100 - clampedNew;

    let newOtherWeights;
    if (otherSumBefore === 0) {
      // Split the remainder equally among others
      newOtherWeights = equalSplit(others.length).map(
        (w) => Math.round((w / 100) * otherSumTarget)
      );
    } else {
      newOtherWeights = others.map((a) =>
        Math.round(((a.weight || 0) / otherSumBefore) * otherSumTarget)
      );
    }

    // Fix rounding so the final total is exactly 100
    let currentTotal = clampedNew + newOtherWeights.reduce((s, w) => s + w, 0);
    let diff = 100 - currentTotal;
    if (diff !== 0 && newOtherWeights.length > 0) {
      // Apply correction to indices with largest absolute weight first
      const order = newOtherWeights
        .map((w, idx) => ({ idx, w }))
        .sort((a, b) => b.w - a.w);
      let i = 0;
      while (diff !== 0 && i < order.length * 4) {
        const targetIdx = order[i % order.length].idx;
        if (diff > 0) {
          newOtherWeights[targetIdx] += 1;
          diff -= 1;
        } else if (newOtherWeights[targetIdx] > 0) {
          newOtherWeights[targetIdx] -= 1;
          diff += 1;
        }
        i += 1;
      }
    }

    let otherIdx = 0;
    return list.map((a) => {
      if (a.anchor_id === changedId) return { ...a, weight: clampedNew };
      const w = newOtherWeights[otherIdx];
      otherIdx += 1;
      return { ...a, weight: w };
    });
  };

  // Debounced batch save of all anchor weights
  const persistWeights = (anchorList) => {
    if (saveWeightsTimerRef.current) clearTimeout(saveWeightsTimerRef.current);
    saveWeightsTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('sage_token');
        await axios.put(
          `${API}/knowledge-anchors/weights`,
          {
            weights: anchorList.map((a) => ({
              anchor_id: a.anchor_id,
              weight: a.weight
            }))
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        toast.error('Failed to save influence allocation');
      }
    }, 400);
  };

  const handleWeightChange = (anchorId, newWeight) => {
    const updated = redistributeWeights(anchors, anchorId, newWeight);
    setAnchors(updated); // real-time UI update
    persistWeights(updated);
  };

  const handleLogout = () => {
    localStorage.removeItem('sage_token');
    localStorage.removeItem('sage_user');
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="overlay-noise" />
        <p className="text-zinc-500 relative z-10">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <div className="overlay-noise" />

      {/* One-time name prompt for users who haven't given SAGE a name yet.
          Shown when the user has loaded, has no name on file, and hasn't
          explicitly skipped the prompt in this browser. */}
      {user && !user.name && !user._namePromptSkipped && !hasDismissedNamePrompt() && (
        <NamePromptModal
          onSaved={handleSaveNameFromPrompt}
          onSkip={handleSkipNamePrompt}
        />
      )}

      {/* One-time soft acknowledgment of Terms + Privacy + Beta
          Confidentiality. Fires on first logged-in Dashboard mount after
          a policy version bump. Declining signs the user out. */}
      {policyState?.needsAck && (
        <PolicyAcknowledgmentModal
          onAgree={handlePolicyAgree}
          onDecline={handlePolicyDecline}
          submitting={policySubmitting}
        />
      )}
      
      <div className="relative z-10">
        {/* Header */}
        <div className="border-b border-white/5 bg-stone-950/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
            <div>
              <h1 
                className="text-3xl font-light tracking-tight text-zinc-100"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                SAGE
              </h1>
              <p className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-1">
                {user?.email}
              </p>
            </div>
            <div className="flex gap-3">
              <ThemeToggle className="border border-white/20 rounded-sm" />
              <TutorialLaunchButton variant="icon" />
              <Button
                onClick={() => setShowSettings(!showSettings)}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-4 py-2 transition-all"
                data-testid="settings-button"
              >
                <Settings className="w-4 h-4" />
              </Button>
              <Button
                onClick={handleLogout}
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-4 py-2 transition-all"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Settings Panel */}
              {showSettings && (
                <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-8" data-testid="settings-panel">
                  <h2 className="text-2xl font-light text-white mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    SAGE Profile
                  </h2>
                  
                  <div className="space-y-8">
                    <div>
                      <label className="text-sm text-zinc-400 block mb-2">
                        Name SAGE calls you
                      </label>
                      <Input
                        value={profile.name || ''}
                        onChange={(e) =>
                          setProfile({ ...profile, name: e.target.value })
                        }
                        placeholder="First name (optional)"
                        maxLength={60}
                        data-testid="settings-name-input"
                        className="bg-black/30 border-white/10 text-white placeholder:text-zinc-600"
                      />
                      <p className="text-xs text-zinc-500 mt-2">
                        Used once at the top of each session. Leave blank for a nameless greeting.
                      </p>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm text-zinc-400">Tone</label>
                        <span className="text-xs text-zinc-400">{profile.voice_intensity < 33 ? 'Gentle & Soft' : profile.voice_intensity < 66 ? 'Casual & Warm' : 'Energetic & Upbeat'}</span>
                      </div>
                      <Slider
                        value={[profile.voice_intensity]}
                        onValueChange={([value]) => setProfile({...profile, voice_intensity: value})}
                        max={100}
                        step={1}
                      />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm text-zinc-400">Style</label>
                        <span className="text-xs text-zinc-400">{profile.stoic_level < 33 ? 'Quick & Practical' : profile.stoic_level < 66 ? 'Balanced' : 'Thoughtful & Reflective'}</span>
                      </div>
                      <Slider
                        value={[profile.stoic_level]}
                        onValueChange={([value]) => setProfile({...profile, stoic_level: value})}
                        max={100}
                        step={1}
                      />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm text-zinc-400">Directness</label>
                        <span className="text-xs text-zinc-400">{profile.directness < 33 ? 'Supportive & Gentle' : profile.directness < 66 ? 'Honest & Kind' : 'Straight-Up Real'}</span>
                      </div>
                      <Slider
                        value={[profile.directness]}
                        onValueChange={([value]) => setProfile({...profile, directness: value})}
                        max={100}
                        step={1}
                      />
                    </div>
                    {/* Hold-to-Talk toggle. When OFF, /live-coaching uses
                        the new hands-free Auto Conversation (ElevenLabs
                        Agents). When ON, the classic press-and-hold mic
                        flow comes back. Default: OFF (Auto). */}
                    <div className="pt-4 border-t border-white/5">
                      <label className="text-sm text-zinc-300 block mb-2">
                        SAGE's voice
                      </label>
                      <p className="text-xs text-zinc-500 mb-3">
                        Pick the voice SAGE uses on Auto Conversation and press-and-hold alike.
                      </p>
                      <div
                        data-testid="voice-chooser"
                        className="grid grid-cols-2 gap-2"
                      >
                        {voiceCatalogue.map((v) => {
                          const active = profile.voice_id === v.voice_id;
                          return (
                            <button
                              key={v.voice_id}
                              type="button"
                              data-testid={`voice-option-${v.voice_id}`}
                              onClick={() => setProfile({ ...profile, voice_id: v.voice_id })}
                              className={`text-left rounded-md border p-2.5 transition-colors ${
                                active
                                  ? 'border-amber-400/60 bg-amber-500/10'
                                  : 'border-white/10 bg-black/20 hover:border-white/20'
                              }`}
                              aria-pressed={active}
                            >
                              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">
                                {v.gender}
                              </div>
                              <div className="text-sm text-white">{v.name}</div>
                              <div className="text-xs text-zinc-400 mt-1 leading-snug">
                                {v.description}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/5">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1">
                          <label className="text-sm text-zinc-300">Hold-to-Talk mode</label>
                          <p className="text-xs text-zinc-500 mt-1">
                            Press and hold a button for every turn, instead of letting SAGE listen continuously.
                            Useful in noisy rooms or when accents are hard to transcribe.
                          </p>
                        </div>
                        <button
                          data-testid="hold-to-talk-toggle"
                          type="button"
                          onClick={() => setProfile({ ...profile, auto_mode_enabled: !profile.auto_mode_enabled })}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                            profile.auto_mode_enabled ? 'bg-white/10' : 'bg-amber-500/60'
                          }`}
                          aria-pressed={!profile.auto_mode_enabled}
                          aria-label="Hold-to-Talk mode"
                        >
                          <span
                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                              profile.auto_mode_enabled ? 'translate-x-0.5' : 'translate-x-[1.375rem]'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleProfileUpdate}
                    className="w-full frost-button rounded-sm px-6 py-3 mt-6"
                  >
                    Save Changes
                  </Button>

                  {/* Re-take the onboarding tour any time. Sits inside the
                      Settings panel so it's discoverable without cluttering
                      the header. */}
                  <div className="mt-6 pt-6 border-t border-white/10 space-y-5">
                    {/* Appearance picker — matches the header toggle but
                        gives new users a labelled, discoverable entry point.
                        Dark remains SAGE's native identity; Light is a
                        warm linen palette for daytime reading. */}
                    <AppearancePicker />
                    <div className="flex justify-center">
                      <TutorialLaunchButton variant="text" />
                    </div>
                  </div>
                </div>
              )}

              <OuraCard />

              {/* Wins & Streak Tracker */}
              {wins && (
                <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6" data-testid="wins-tracker">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      Your Wins
                    </h3>
                    <Trophy className="w-5 h-5 text-yellow-500" />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center p-3 bg-black/20 rounded-sm">
                      <p className="text-2xl font-light text-white">{wins.total_wins}</p>
                      <p className="text-xs text-zinc-500">Total Wins</p>
                    </div>
                    <div className="text-center p-3 bg-black/20 rounded-sm">
                      <p className="text-2xl font-light text-white">{wins.wins_this_week}</p>
                      <p className="text-xs text-zinc-500">This Week</p>
                    </div>
                    <div className="text-center p-3 bg-black/20 rounded-sm">
                      <div className="flex items-center justify-center gap-1">
                        <Flame className="w-4 h-4 text-orange-500" />
                        <p className="text-2xl font-light text-white">{wins.current_streak}</p>
                      </div>
                      <p className="text-xs text-zinc-500">Day Streak</p>
                    </div>
                  </div>
                  
                  {wins.best_streak > 0 && (
                    <p className="text-xs text-zinc-400 text-center">
                      Best streak: {wins.best_streak} days
                    </p>
                  )}
                </div>
              )}

              {/* Pending Commitments */}
              {todos.length > 0 && (
                <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6" data-testid="pending-todos">
                  <h3 className="text-xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Your Commitments
                  </h3>
                  <div className="space-y-3">
                    {todos.slice(0, 5).map((todo) => (
                      <div 
                        key={todo.todo_id}
                        className="flex items-start gap-3 p-3 bg-black/20 rounded-sm group hover:bg-black/30 transition-all"
                      >
                        <button
                          onClick={() => completeTodo(todo.todo_id)}
                          className="mt-0.5 text-zinc-400 hover:text-green-500 transition-colors"
                          data-testid={`complete-todo-${todo.todo_id}`}
                        >
                          <Circle className="w-5 h-5" />
                        </button>
                        <p className="text-sm text-zinc-300 flex-1">{todo.description}</p>
                      </div>
                    ))}
                  </div>
                  {todos.length > 5 && (
                    <p className="text-xs text-zinc-400 text-center mt-3">
                      +{todos.length - 5} more commitments
                    </p>
                  )}
                </div>
              )}

              {/* Live Coaching Interface */}
              <div 
                className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-12 text-center cursor-pointer hover:border-white/10 transition-all"
                onClick={() => navigate('/live-coaching')}
                data-testid="live-coaching-card"
              >
                <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center relative overflow-hidden group">
                  {/* Animated rings */}
                  <div className="absolute inset-0 rounded-full bg-white/5 animate-ping" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-4 rounded-full bg-white/10 animate-pulse" style={{ animationDuration: '2s' }}></div>
                  <div className="absolute inset-8 rounded-full bg-white/20"></div>
                </div>
                <h3 className="text-2xl font-light text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Live Coaching
                </h3>
                <p className="text-zinc-500 mb-4">Voice-to-voice wisdom with SAGE</p>
                <Button className="threshold-button rounded-sm px-6 py-3 text-base">
                  <span>Start Session</span>
                  <span className="threshold-button-arrow" aria-hidden="true">→</span>
                </Button>
                {/* Beta usage meter — minutes used / remaining / reset
                    date, plus a 'request more time' CTA that opens
                    once the user approaches or crosses their monthly
                    cap. Clicking anywhere inside the meter must NOT
                    navigate to /live-coaching, so we stop propagation
                    on the wrapper. */}
                <div onClick={(e) => e.stopPropagation()}>
                  <AutoUsageMeter />
                </div>
              </div>

              {/* Learn Mode */}
              <div
                className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-8 cursor-pointer hover:border-white/10 transition-all flex items-center gap-6"
                onClick={() => navigate('/learn')}
                data-testid="learn-mode-card"
              >
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-500/5 flex items-center justify-center shrink-0 border border-amber-500/20">
                  <BookOpen className="w-7 h-7 text-amber-300" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-light text-white mb-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    Learn Mode
                  </h3>
                  <p className="text-sm text-zinc-400">
                    Upload material on any topic — get a diagnostic, a personalized plan, and a Socratic tutor.
                  </p>
                </div>
                <span className="text-zinc-400 shrink-0">→</span>
              </div>

              {/* Session Archive Link */}
              <div 
                className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6 cursor-pointer hover:border-white/10 transition-all flex items-center justify-between"
                onClick={() => navigate('/sessions')}
                data-testid="session-archive-link"
              >
                <div className="flex items-center gap-3">
                  <Archive className="w-5 h-5 text-zinc-500" />
                  <div>
                    <h3 className="text-lg font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      Session Archive
                    </h3>
                    <p className="text-xs text-zinc-400">View past sessions & summaries</p>
                  </div>
                </div>
                <span className="text-zinc-400">→</span>
              </div>

              {/* Search Link */}
              <div 
                className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6 cursor-pointer hover:border-white/10 transition-all flex items-center justify-between"
                onClick={() => navigate('/search')}
                data-testid="search-link"
              >
                <div className="flex items-center gap-3">
                  <Search className="w-5 h-5 text-zinc-500" />
                  <div>
                    <h3 className="text-lg font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                      Search
                    </h3>
                    <p className="text-xs text-zinc-400">Find insights & knowledge</p>
                  </div>
                </div>
                <span className="text-zinc-400">→</span>
              </div>
            </div>

            {/* Knowledge Anchors Sidebar */}
            <div className="space-y-6">
              <div className="bg-stone-950/50 border border-white/5 backdrop-blur-sm rounded-sm p-6" data-testid="knowledge-anchors-panel">
                <h3 className="text-xl font-light text-white mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Knowledge Anchors
                </h3>
                <p className="text-xs text-zinc-400 mb-6">
                  Upload wisdom to guide SAGE's responses
                </p>

                {/* Upload Buttons */}
                <div className="space-y-3 mb-6">
                  <label className="block cursor-pointer">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="pdf-upload-input"
                    />
                    <div className="w-full frost-button rounded-sm px-4 py-3 gap-2">
                      <Upload className="w-4 h-4" />
                      Upload PDF
                    </div>
                  </label>
                  <Button
                    onClick={() => setShowAddText(!showAddText)}
                    className="w-full bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-sm px-4 py-3 transition-all flex items-center justify-center gap-2"
                    data-testid="add-text-button"
                  >
                    <FileText className="w-4 h-4" />
                    Add Text
                  </Button>
                </div>

                {/* Add Text Form */}
                {showAddText && (
                  <div className="mb-6 space-y-3 p-4 bg-black/20 rounded-sm border border-white/5">
                    <Input
                      placeholder="Title"
                      value={textAnchor.title}
                      onChange={(e) => setTextAnchor({...textAnchor, title: e.target.value})}
                      className="bg-transparent border-b border-white/20 text-white"
                      data-testid="text-anchor-title-input"
                    />
                    <textarea
                      placeholder="Your wisdom here..."
                      value={textAnchor.content}
                      onChange={(e) => setTextAnchor({...textAnchor, content: e.target.value})}
                      className="w-full bg-transparent border border-white/20 text-white p-3 rounded-sm min-h-32 text-sm"
                      data-testid="text-anchor-content-input"
                    />
                    <Button
                      onClick={handleTextAnchorSubmit}
                      className="w-full frost-button rounded-sm px-4 py-2 text-sm"
                      data-testid="submit-text-anchor-button"
                    >
                      Add Anchor
                    </Button>
                  </div>
                )}

                {/* Anchors List */}
                <div className="space-y-3" data-testid="anchors-list">
                  {anchors.length === 0 ? (
                    <p className="text-xs text-zinc-400 text-center py-8">No anchors yet</p>
                  ) : (
                    anchors.map((anchor) => (
                      <div 
                        key={anchor.anchor_id} 
                        className="bg-black/20 rounded-sm p-4 border border-white/5 group hover:border-white/10 transition-all"
                        data-testid={`anchor-${anchor.anchor_id}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <p className="text-sm text-white font-medium mb-1">{anchor.title}</p>
                            <p className="text-xs text-zinc-400 font-mono">{anchor.source_type.toUpperCase()}</p>
                          </div>
                          <button
                            onClick={() => handleDeleteAnchor(anchor.anchor_id)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-zinc-400 hover:text-red-400"
                            data-testid={`delete-anchor-${anchor.anchor_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Weight Slider */}
                        <div className="mt-2">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-zinc-500">Influence</span>
                            <span className="text-xs text-zinc-400 font-mono">{anchor.weight || 50}%</span>
                          </div>
                          <Slider
                            value={[anchor.weight || 50]}
                            onValueChange={([value]) => handleWeightChange(anchor.anchor_id, value)}
                            max={100}
                            step={5}
                            className="w-full"
                            data-testid={`weight-slider-${anchor.anchor_id}`}
                          />
                          <div className="flex justify-between text-[10px] text-zinc-400 mt-1">
                            <span>Reference</span>
                            <span>Primary</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Reference Documents — concrete artifacts (manuscripts,
                  notes, ideas) SAGE can discuss when attached to a session.
                  Lives here beside Knowledge Anchors because both feed
                  SAGE material, but their roles differ (anchors = voice,
                  documents = topic). */}
              <ReferenceDocsPanel />
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    </div>
  );
};

export default DashboardNew;
